import React, { useCallback, useId, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { TagColored } from '@spark';
import FieldLabel from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/FieldLabel';
import NerEntityTypeMenu from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/NerEntityTypeMenu';
import {
    addOrReplaceEntity,
    applyBackwardDelete,
    applyForwardDelete,
    applyInsert,
    applyRangeReplace,
    buildRenderSegments,
    caretOffsetIn,
    hasOverlap,
    setCaretIn,
    tokenizePlain,
    trimRange,
} from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/nerExampleSpans';
import { getSparkTagVariant } from '@/features/datasets/components/DataSelection/sparkLabelColors';

const DRAG_THRESHOLD_PX = 3;

const rectFromDom = (rect) => ({
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
});

/**
 * Contenteditable example field that reuses Spark InputTextArea chrome and
 * lets the user tag word or phrase spans with entity types.
 *
 * The optional `getEntityMeta` / `onToggleEntityValidated` / `onRemoveEntity`
 * props (annotate-modal usage) surface Validate/Remove actions in the entity
 * popover for already-tagged spans; when absent the editor behaves exactly as
 * in the data-collection flows.
 */
const NerExampleEditor = ({
    label,
    required = false,
    help,
    placeholder,
    height = 110,
    text,
    entities,
    entityNames,
    onChange,
    error = false,
    disabled = false,
    dir,
    getEntityMeta,
    onToggleEntityValidated,
    onRemoveEntity,
}) => {
    const fieldId = useId();
    const editorRef = useRef(null);
    const mouseDownPos = useRef(null);
    const pendingCaret = useRef(null);
    const keyInsertRef = useRef(false);
    const [menu, setMenu] = useState(null);

    const names = entityNames || [];
    const currentEntities = entities || [];
    const fillClass = text ? 'spark-input-textarea-fill-filled' : 'spark-input-textarea-fill-empty';

    const closeMenu = useCallback(() => setMenu(null), []);

    useLayoutEffect(() => {
        if (!pendingCaret.current || !editorRef.current) return;
        const { start, end } = pendingCaret.current;
        setCaretIn(editorRef.current, start, end);
        pendingCaret.current = null;
    }, [text, currentEntities]);

    const commit = (result) => {
        pendingCaret.current = {
            start: result.selectionStart ?? result.caret,
            end: result.selectionEnd ?? result.caret,
        };
        onChange({ text: result.text, entities: result.entities });
    };

    const selectionRange = () => {
        const root = editorRef.current;
        const length = (text || '').length;
        if (!root) return [length, length];
        const selection = window.getSelection();
        const anchor = selection?.anchorNode;
        const focus = selection?.focusNode;
        const inEditor =
            !!selection &&
            selection.rangeCount > 0 &&
            (anchor === root || root.contains(anchor)) &&
            (focus === root || root.contains(focus));
        if (!inEditor) return [length, length];
        const start = Math.max(0, Math.min(caretOffsetIn(root, 'start'), length));
        const end = Math.max(0, Math.min(caretOffsetIn(root, 'end'), length));
        return start <= end ? [start, end] : [end, start];
    };

    const openMenuAt = (rect, range, selectedType = null) => {
        if (!range || range.start >= range.end) return;
        setMenu({
            open: true,
            rect: rectFromDom(rect),
            start: range.start,
            end: range.end,
            selectedType,
        });
    };

    const insertAtSelection = (chunk) => {
        const [selStart, selEnd] = selectionRange();
        let nextText = text;
        let nextEntities = currentEntities;
        let caret = selStart;
        if (selStart !== selEnd) {
            const cleared = applyRangeReplace(text, currentEntities, selStart, selEnd, '');
            nextText = cleared.text;
            nextEntities = cleared.entities;
            caret = selStart;
        }
        commit(applyInsert(nextText, nextEntities, caret, chunk));
    };

    const handleBeforeInput = (event) => {
        if (disabled || event.isComposing || event.nativeEvent?.isComposing) return;
        const native = event.nativeEvent || event;
        const inputType = native.inputType || '';
        // Let onPaste own clipboard insertion; canceling beforeinput can drop the paste event.
        if (inputType === 'insertFromPaste' || inputType === 'insertFromDrop') return;
        if (keyInsertRef.current) {
            event.preventDefault();
            return;
        }
        const data = event.data ?? native.data ?? '';
        // React's onBeforeInput is often a textInput polyfill with no inputType.
        const insertChunk =
            inputType === 'insertText' || inputType === 'insertReplacementText' || (!inputType && data)
                ? data || ''
                : inputType === 'insertParagraph' || inputType === 'insertLineBreak'
                  ? '\n'
                  : null;

        if (insertChunk != null) {
            event.preventDefault();
            insertAtSelection(insertChunk);
            return;
        }
        const [selStart, selEnd] = selectionRange();
        if (inputType === 'deleteContentBackward' || inputType === 'deleteByCut' || inputType === 'deleteContent') {
            event.preventDefault();
            commit(applyBackwardDelete(text, currentEntities, selStart, selEnd));
            return;
        }
        if (inputType === 'deleteContentForward') {
            event.preventDefault();
            commit(applyForwardDelete(text, currentEntities, selStart, selEnd));
        }
    };

    const handlePaste = (event) => {
        if (disabled) return;
        event.preventDefault();
        const pasted =
            event.clipboardData?.getData?.('text/plain') ??
            event.nativeEvent?.clipboardData?.getData?.('text/plain') ??
            '';
        const [selStart, selEnd] = selectionRange();
        let nextText = text;
        let nextEntities = currentEntities;
        if (selStart !== selEnd) {
            const cleared = applyRangeReplace(text, currentEntities, selStart, selEnd, '');
            nextText = cleared.text;
            nextEntities = cleared.entities;
        }
        commit(applyInsert(nextText, nextEntities, selStart, pasted));
    };

    const handleKeyDown = (event) => {
        if (disabled || event.isComposing || event.nativeEvent?.isComposing) return;
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
            keyInsertRef.current = true;
            event.preventDefault();
            insertAtSelection(event.key);
            window.setTimeout(() => {
                keyInsertRef.current = false;
            }, 0);
            return;
        }
        if (event.key === 'Enter') {
            keyInsertRef.current = true;
            event.preventDefault();
            insertAtSelection('\n');
            window.setTimeout(() => {
                keyInsertRef.current = false;
            }, 0);
            return;
        }
        if (event.key !== 'Backspace' && event.key !== 'Delete') return;
        event.preventDefault();
        const [selStart, selEnd] = selectionRange();
        if (event.key === 'Backspace') {
            commit(applyBackwardDelete(text, currentEntities, selStart, selEnd));
            return;
        }
        commit(applyForwardDelete(text, currentEntities, selStart, selEnd));
    };

    const handleInput = () => {
        if (disabled || currentEntities.length > 0 || keyInsertRef.current) return;
        const root = editorRef.current;
        if (!root) return;
        const nextText = (root.innerText || root.textContent || '').replace(/\u00A0/g, ' ');
        // Empty contenteditable with a caret <br> reports a lone newline in some browsers.
        const normalized = nextText === '\n' && !text ? '' : nextText;
        if (normalized === text) return;
        const caret = Math.max(0, Math.min(caretOffsetIn(root, 'end'), normalized.length));
        pendingCaret.current = { start: caret, end: caret };
        onChange({ text: normalized, entities: [] });
    };

    const handleMouseDown = (event) => {
        mouseDownPos.current = { x: event.clientX, y: event.clientY };
    };

    const movedPastThreshold = (event) => {
        const down = mouseDownPos.current;
        if (!down) return false;
        const dx = event.clientX - down.x;
        const dy = event.clientY - down.y;
        return Math.hypot(dx, dy) > DRAG_THRESHOLD_PX;
    };

    const handleMouseUp = (event) => {
        if (disabled) return;
        const root = editorRef.current;
        const selection = window.getSelection();
        if (!root || !selection || selection.isCollapsed || selection.rangeCount === 0) return;
        if (!root.contains(selection.anchorNode) || !root.contains(selection.focusNode)) return;
        const [selStart, selEnd] = selectionRange();
        if (selStart === selEnd) return;
        const trimmed = trimRange(text, selStart, selEnd);
        if (trimmed.start >= trimmed.end) return;
        if (hasOverlap(currentEntities, trimmed)) return;
        const rect = selection.getRangeAt(0).getBoundingClientRect();
        openMenuAt(rect.width || rect.height ? rect : event.currentTarget.getBoundingClientRect(), trimmed, null);
    };

    const handleTokenClick = (event, start, end) => {
        if (disabled || movedPastThreshold(event)) return;
        event.preventDefault();
        openMenuAt(event.currentTarget.getBoundingClientRect(), { start, end }, null);
    };

    const handleSelectType = (type) => {
        if (!menu) return;
        onChange({
            text,
            entities: addOrReplaceEntity(currentEntities, { type, start: menu.start, end: menu.end }),
        });
        setMenu(null);
        window.getSelection()?.removeAllRanges();
    };

    const renderPlain = (from, to, keyPrefix) =>
        tokenizePlain(text, from, to).map((token) => {
            const slice = text.slice(token.start, token.end);
            if (token.kind === 'word') {
                return (
                    <span
                        key={`${keyPrefix}-${token.start}`}
                        className="gdai-ner-token"
                        data-start={token.start}
                        data-end={token.end}
                        onClick={(event) => handleTokenClick(event, token.start, token.end)}
                    >
                        {slice}
                    </span>
                );
            }
            return (
                <span key={`${keyPrefix}-${token.start}`} data-start={token.start}>
                    {slice}
                </span>
            );
        });

    const segments = buildRenderSegments(text, currentEntities);

    // Validate/Remove actions for an already-tagged span (annotate modal only).
    const menuEntityActions =
        menu?.selectedType && !disabled && (onToggleEntityValidated || onRemoveEntity)
            ? {
                  validated: getEntityMeta ? !!getEntityMeta(menu.start, menu.end)?.validated : undefined,
                  onToggleValidated: onToggleEntityValidated
                      ? () => {
                            onToggleEntityValidated({ start: menu.start, end: menu.end });
                            setMenu(null);
                        }
                      : undefined,
                  onRemove: onRemoveEntity
                      ? () => {
                            onRemoveEntity({ start: menu.start, end: menu.end });
                            setMenu(null);
                        }
                      : undefined,
              }
            : null;

    return (
        <div className={`gdai-field${error ? ' gdai-field--error' : ''}`}>
            {label ? <FieldLabel text={label} required={required} help={help} htmlFor={fieldId} /> : null}
            <div
                className={`spark-input-textarea spark-input-textarea-medium ${fillClass} gdai-textarea`}
                style={{ '--gdai-textarea-height': `${height}px` }}
            >
                <div className="spark-input-textarea-wrap">
                    <div
                        className="spark-input-textarea-box"
                        onMouseDown={(event) => {
                            if (disabled) return;
                            const root = editorRef.current;
                            if (!root) return;
                            if (event.target === root || root.contains(event.target)) return;
                            root.focus();
                        }}
                    >
                        {text ? null : (
                            <span className="gdai-ner-editor__placeholder" aria-hidden="true">
                                {placeholder}
                            </span>
                        )}
                        <div
                            ref={editorRef}
                            id={fieldId}
                            role="textbox"
                            dir={dir}
                            tabIndex={disabled ? -1 : 0}
                            aria-multiline="true"
                            aria-invalid={error || undefined}
                            aria-disabled={disabled || undefined}
                            contentEditable={!disabled}
                            suppressContentEditableWarning
                            className="spark-input-textarea-field gdai-ner-editor"
                            placeholder={placeholder}
                            data-placeholder={placeholder}
                            spellCheck={false}
                            onBeforeInput={handleBeforeInput}
                            onInput={handleInput}
                            onPaste={handlePaste}
                            onKeyDown={handleKeyDown}
                            onMouseDown={handleMouseDown}
                            onMouseUp={handleMouseUp}
                            onFocus={() => {
                                const root = editorRef.current;
                                if (!root) return;
                                const selection = window.getSelection();
                                const node = selection?.anchorNode;
                                if (node === root || (node && root.contains(node))) return;
                                setCaretIn(root, (text || '').length);
                            }}
                        >
                            {text
                                ? segments.map((segment) => {
                                      if (segment.kind === 'entity') {
                                          const slice = text.slice(segment.start, segment.end);
                                          // Unvalidated affordance (annotate modal only): dashed
                                          // warning border, same as the legacy tagger surface.
                                          const meta = getEntityMeta
                                              ? getEntityMeta(segment.start, segment.end)
                                              : null;
                                          const unvalidated = meta ? meta.validated === false : false;
                                          return (
                                              <span
                                                  key={`e-${segment.start}`}
                                                  data-start={segment.start}
                                                  data-type={segment.type}
                                                  className="gdai-ner-entity"
                                                  onMouseEnter={(event) => {
                                                      if (disabled || event.buttons === 1) return;
                                                      openMenuAt(
                                                          event.currentTarget.getBoundingClientRect(),
                                                          { start: segment.start, end: segment.end },
                                                          segment.type
                                                      );
                                                  }}
                                                  onClick={(event) => event.stopPropagation()}
                                              >
                                                  <TagColored
                                                      noPadding
                                                      label={slice}
                                                      variant={getSparkTagVariant(segment.type, names)}
                                                      className={
                                                          unvalidated
                                                              ? 'gdai-ner-entity-tag--unvalidated'
                                                              : undefined
                                                      }
                                                  />
                                              </span>
                                          );
                                      }
                                      return renderPlain(segment.start, segment.end, `t-${segment.start}`);
                                  })
                                : null}
                        </div>
                    </div>
                    <span className="spark-input-textarea-focus" aria-hidden="true" />
                </div>
            </div>
            <NerEntityTypeMenu
                open={!!menu?.open}
                anchorRect={menu?.rect}
                entityNames={names}
                selectedType={menu?.selectedType ?? null}
                onSelect={handleSelectType}
                onClose={closeMenu}
                anchorEl={editorRef.current}
                entityActions={menuEntityActions}
            />
        </div>
    );
};

NerExampleEditor.propTypes = {
    label: PropTypes.string,
    required: PropTypes.bool,
    help: PropTypes.string,
    placeholder: PropTypes.string,
    height: PropTypes.number,
    text: PropTypes.string,
    entities: PropTypes.arrayOf(
        PropTypes.shape({
            type: PropTypes.string.isRequired,
            start: PropTypes.number.isRequired,
            end: PropTypes.number.isRequired,
        })
    ),
    entityNames: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.func.isRequired,
    error: PropTypes.bool,
    disabled: PropTypes.bool,
    dir: PropTypes.oneOf(['ltr', 'rtl', 'auto']),
    getEntityMeta: PropTypes.func,
    onToggleEntityValidated: PropTypes.func,
    onRemoveEntity: PropTypes.func,
};

export default NerExampleEditor;
